// Renders a single contract-v2 envelope by routing artifact_type to a viz
// widget. Also renders the meta-row (title + freshness + git_sha + tool),
// warnings, structured_diff, pair-hints, and links.

import {
  EntityTable, Markdown, Code, Chart, PropertyGrid, Tree, Timeline, KpiRow, Header,
} from "@viz/widgets";
import type { Entity } from "@viz/types";
import type { Envelope, TableData, HtmlData, ImageData } from "../lib/envelope.js";
import { GanttWidget } from "./widgets/GanttWidget.js";
import { HeatmapWidget } from "./widgets/HeatmapWidget.js";
import { WbsWidget } from "./widgets/WbsWidget.js";
import { NetworkGraphWidget } from "./widgets/NetworkGraphWidget.js";

interface Props {
  envelope: Envelope;
  onPairClick?: (toolName: string) => void;
}

export function ArtifactView({ envelope, onPairClick }: Props) {
  // Widgets that paint into a sized canvas (Gantt, Heatmap, NetworkGraph)
  // need a definite parent width/height — wrapping them in `overflow-auto`
  // collapses the inner SVG/canvas to 0 width. Use `overflow-hidden` for
  // those; keep auto-scroll for tables, JSON, markdown, etc.
  const usesNativeCanvas = ["gantt-data", "risk-heatmap", "dependency-network", "wbs-tree"]
    .includes(envelope.artifact_type);
  const bodyOverflow = usesNativeCanvas ? "overflow-hidden" : "overflow-auto";
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <MetaRow envelope={envelope} />
      <Warnings envelope={envelope} />
      <StructuredDiff envelope={envelope} />
      <div className={`flex-1 min-h-0 ${bodyOverflow}`}>
        <Body envelope={envelope} />
      </div>
      <PairHints envelope={envelope} onPairClick={onPairClick} />
      <Links envelope={envelope} />
    </div>
  );
}

function MetaRow({ envelope }: { envelope: Envelope }) {
  const freshness = envelope.audit?.freshness ?? "unknown";
  const freshClass =
    freshness === "live"  ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    freshness === "stale" ? "bg-amber-50  text-amber-700  border-amber-200" :
                            "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-900">{envelope.title}</span>
        <span className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase ${freshClass}`}>
          {freshness}
        </span>
      </div>
      <div className="font-mono text-[11px] text-slate-500">
        {envelope.audit?.tool ?? "?"} · git {(envelope.audit?.git_sha ?? "?").slice(0, 8)}
      </div>
    </div>
  );
}

function Warnings({ envelope }: { envelope: Envelope }) {
  if (!envelope.warnings?.length) return null;
  return (
    <div className="space-y-1 border-b border-slate-200 bg-slate-50 px-4 py-2">
      {envelope.warnings.map((w, i) => {
        const sev = typeof w === "string" ? "warn" : (w.severity ?? "warn");
        const msg = typeof w === "string" ? w : (w.severity ? `[${w.severity}] ` : "") + w.message + (w.locus ? ` @ ${w.locus}` : "");
        const cls =
          sev === "error" ? "border-rose-400 bg-rose-50 text-rose-700" :
          sev === "info"  ? "border-sky-400  bg-sky-50  text-sky-700" :
                            "border-amber-400 bg-amber-50 text-amber-700";
        return (
          <div key={i} className={`border-l-2 px-3 py-1 text-xs ${cls}`}>{msg}</div>
        );
      })}
    </div>
  );
}

function StructuredDiff({ envelope }: { envelope: Envelope }) {
  if (!envelope.structured_diff) return null;
  const sd = envelope.structured_diff;
  return (
    <div className="border-l-2 border-emerald-400 bg-emerald-50 px-4 py-2 font-mono text-xs">
      <div className="text-[10px] font-bold uppercase text-emerald-700">{sd.kind}</div>
      <div className="text-slate-600">{sd.file} · {sd.section}</div>
      <div className="text-rose-700">- {safeJson(sd.before)}</div>
      <div className="text-emerald-700">+ {safeJson(sd.after)}</div>
    </div>
  );
}

function Body({ envelope }: { envelope: Envelope }) {
  switch (envelope.artifact_type) {
    case "html": {
      const data = (envelope.data ?? {}) as HtmlData;
      if (data.url) {
        return (
          <iframe
            src={data.url}
            title={envelope.title}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
            referrerPolicy="no-referrer"
          />
        );
      }
      if (data.html) {
        return (
          <iframe
            srcDoc={data.html}
            title={envelope.title}
            sandbox="allow-scripts allow-same-origin"
            style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
          />
        );
      }
      return <div className="p-6 text-sm text-slate-500">html envelope without url or html</div>;
    }
    case "table": {
      const data = (envelope.data ?? { columns: [], rows: [] }) as TableData;
      // Adapt {columns, rows} → EntityTable's {entities: Entity[]} shape so
      // the vendored TanStack-backed widget renders it natively.
      const entities: Entity[] = (data.rows ?? []).map((row, i) => ({
        id: String(row.id ?? row.name ?? row.task_id ?? row.risk_id ?? i),
        type: "row",
        tags: [],
        parents: [],
        refs: [],
        props: Object.fromEntries(
          (data.columns ?? Object.keys(row)).map(c => [c, { type: "string", value: row[c] }]),
        ),
      }));
      return <EntityTable kind="entity-table" props={{ entities }} />;
    }
    case "markdown": {
      const content = typeof envelope.data === "string" ? envelope.data : String(envelope.data ?? "");
      return <Markdown kind="markdown" props={{ content }} />;
    }
    case "image": {
      const data = (envelope.data ?? {}) as ImageData;
      const src = data.image_url ?? (data.image_base64 ? `data:image/png;base64,${data.image_base64}` : undefined);
      if (!src) return <div className="p-6 text-sm text-slate-500">image envelope without source</div>;
      return <img src={src} alt={envelope.title} className="mx-auto max-h-full max-w-full p-4" />;
    }
    case "text": {
      const content = typeof envelope.data === "string" ? envelope.data : String(envelope.data ?? "");
      return (
        <pre className="m-0 whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed text-slate-800">
          {content}
        </pre>
      );
    }
    case "evm-curve": {
      // EVM data convention: { dates: [...], pv: [...], ev: [...], ac: [...] }
      // or { rows: [{date, pv, ev, ac}, ...] }. Reduce to recharts shape.
      const raw = envelope.data as any;
      const rows: Array<Record<string, unknown>> = Array.isArray(raw?.rows)
        ? raw.rows
        : Array.isArray(raw?.dates)
          ? raw.dates.map((d: string, i: number) => ({
              date: d,
              pv: raw.pv?.[i], ev: raw.ev?.[i], ac: raw.ac?.[i],
            }))
          : [];
      if (!rows.length) {
        return <Code kind="code" props={{ code: safeJson(envelope.data), language: "json" }} />;
      }
      return <Chart kind="chart" props={{ data: rows, kind: "line", x: "date", y: ["pv", "ev", "ac"] }} />;
    }
    case "criticality-curve": {
      // {points: [{x, y, ...}]} or {rows: [{...}]} — recharts line. Falls
      // back to Code if the shape doesn't fit.
      const raw = envelope.data as any;
      const rows: Array<Record<string, unknown>> = Array.isArray(raw?.points)
        ? raw.points
        : Array.isArray(raw?.rows)
          ? raw.rows
          : [];
      if (!rows.length) {
        return <Code kind="code" props={{ code: safeJson(envelope.data), language: "json" }} />;
      }
      const sample = rows[0] ?? {};
      const xKey = "x" in sample ? "x" : "date" in sample ? "date" : Object.keys(sample)[0] ?? "x";
      const yKeys = Object.keys(sample).filter((k) => k !== xKey);
      return <Chart kind="chart" props={{ data: rows, kind: "line", x: xKey, y: yKeys }} />;
    }
    case "gantt-data":
      return <GanttWidget data={envelope.data} title={envelope.title} />;
    case "wbs-tree":
      return <WbsWidget data={envelope.data} />;
    case "risk-heatmap":
      return <HeatmapWidget data={envelope.data} />;
    case "dependency-network":
      return <NetworkGraphWidget data={envelope.data} />;
    case "tree": {
      // Envelope: { nodes: [{ label, kind, children?, value? }] }.
      // Adapt into the vendored Tree widget's {roots, childrenOf} contract.
      const raw = envelope.data as any;
      const nodes: any[] = Array.isArray(raw?.nodes) ? raw.nodes : [];
      type TreeNode = { id: string; label: string; kind: string; value?: unknown; children?: TreeNode[] };
      let idSeq = 0;
      const indexed: TreeNode[] = nodes.map((n, i) => indexNode(n, `n${i}`));
      function indexNode(n: any, baseId: string): TreeNode {
        idSeq++;
        const id = baseId + "/" + idSeq;
        const children: TreeNode[] = Array.isArray(n.children)
          ? n.children.map((c: any, j: number) => indexNode(c, id + "_" + j))
          : [];
        return {
          id,
          label: String(n.label ?? "(unnamed)"),
          kind: String(n.kind ?? "node"),
          value: n.value,
          children,
        };
      }
      const flat = new Map<string, TreeNode>();
      function walk(n: TreeNode) {
        flat.set(n.id, n);
        (n.children ?? []).forEach(walk);
      }
      indexed.forEach(walk);
      const toEntity = (n: TreeNode): Entity => ({
        id: n.id,
        type: n.kind,
        tags: [],
        parents: [],
        refs: [],
        props: {
          name: { type: "string", value: n.label + (n.value !== undefined ? ` = ${safeJson(n.value)}` : "") },
        },
      });
      const roots = indexed.map(toEntity);
      const childrenOf = (id: string): Entity[] => (flat.get(id)?.children ?? []).map(toEntity);
      return <Tree kind="tree" props={{ roots, childrenOf }} />;
    }
    case "timeline": {
      // Envelope: { events: [{ id, title, time, kind?, body?, color? }] }
      const raw = envelope.data as any;
      const events = Array.isArray(raw?.events) ? raw.events : Array.isArray(raw) ? raw : [];
      return <Timeline kind="timeline" props={{ events }} />;
    }
    case "kpi-row": {
      // Envelope: { items: [{ label, value, unit?, tone? }] }
      const raw = envelope.data as any;
      const items = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
      return <KpiRow kind="kpi-row" props={{ items }} />;
    }
    case "property-grid": {
      const raw = (envelope.data ?? {}) as Record<string, unknown>;
      return <PropertyGrid kind="property-grid" props={{ data: raw }} />;
    }
    case "header": {
      const raw = (envelope.data ?? {}) as { title?: string; subtitle?: string; accent?: string };
      return <Header kind="header" props={{ title: raw.title ?? envelope.title, subtitle: raw.subtitle, accent: raw.accent }} />;
    }
    case "container":
    case "tabs":
    case "split": {
      // Composite envelopes — `data.children` is an array of child envelopes.
      // The new shell will resolve these; for inline rendering, we surface the
      // raw structure with a note so it's obvious the composite isn't expanded.
      return (
        <div className="space-y-2 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-400">
            {envelope.artifact_type} envelope — composite layout (resolved by Shell, not ArtifactView)
          </div>
          <Code kind="code" props={{ code: safeJson(envelope.data), language: "json" }} />
        </div>
      );
    }
    case "json":
    default: {
      // PropertyGrid for flat dict-of-scalars; Code (json) for everything else.
      if (envelope.artifact_type === "json" && isFlatDict(envelope.data)) {
        return <PropertyGrid kind="property-grid" props={{ data: envelope.data as Record<string, unknown> }} />;
      }
      return <Code kind="code" props={{ code: safeJson(envelope.data), language: "json" }} />;
    }
  }
}

function PairHints({ envelope, onPairClick }: { envelope: Envelope; onPairClick?: (t: string) => void }) {
  const pairs = envelope.render_hints?.pairs_with ?? [];
  if (!pairs.length) return null;
  return (
    <div className="border-t border-dashed border-slate-200 px-4 py-2 text-xs text-slate-400">
      <span className="mr-2 text-slate-500">Pairs well with:</span>
      {pairs.map(p => (
        <button
          key={p}
          onClick={() => onPairClick?.(p)}
          className="mr-2 rounded border border-slate-200 px-2 py-0.5 font-mono text-[11px] text-sky-700 hover:border-sky-300 hover:bg-sky-50"
        >{p}</button>
      ))}
    </div>
  );
}

function Links({ envelope }: { envelope: Envelope }) {
  const links = envelope.links ?? [];
  if (!links.length) return null;
  return (
    <div className="border-t border-dashed border-slate-200 px-4 py-2 text-xs text-slate-400">
      <span className="mr-2 text-slate-500">Links:</span>
      {links.map(l => (
        <a key={l.href} href={l.href} target="_blank" rel="noreferrer"
           className="mr-3 text-sky-700 underline">{l.label}</a>
      ))}
    </div>
  );
}

function safeJson(v: unknown): string {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function isFlatDict(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  for (const val of Object.values(v as Record<string, unknown>)) {
    if (val !== null && typeof val === "object" && !Array.isArray(val)) {
      // Allow {value, unit} sub-objects per PropertyGrid convention.
      const sub = val as Record<string, unknown>;
      if (!("value" in sub)) return false;
    }
  }
  return true;
}
